import { useCallback, useMemo } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import { TMetricQueryParams } from 'types/GlobalMetric'
import { PeriodFilter } from 'types/GlobalWidget'

const PARAM_KEYS = [
  'filter',
  'from_date',
  // REFERENCE-LINES
  'line_1',
  'line_1_label',
  'line_2',
  'line_2_label',
  'period_filter',
  'real_estates',
  'segment_by',
  'to_date'
] as const

// REFERENCE-LINES
const parseLine = (raw: string | null): number | undefined => {
  if (raw === null || raw === '') return undefined

  const parsed = Number(raw)

  return Number.isFinite(parsed) ? parsed : undefined
}

/**
 * Persists TMetricQueryParams in URL query params.
 * Each field maps to its own URL key for readable URLs.
 * All updates are batched into a single history.replace call.
 */
export function useMetricQueryParams(): [
  TMetricQueryParams,
  (params: TMetricQueryParams) => void
  ] {
  const location = useLocation()
  const history = useHistory()

  const queryParams = useMemo<TMetricQueryParams>(() => {
    const search = new URLSearchParams(location.search)
    const params: TMetricQueryParams = {}

    const filterRaw = search.get('filter')

    if (filterRaw) {
      try {
        params.filter = JSON.parse(filterRaw)
      } catch {
        // ignore malformed filter in URL
      }
    }

    const fromDate = search.get('from_date')
    // REFERENCE-LINES
    const line1 = parseLine(search.get('line_1'))
    const line1Label = search.get('line_1_label')
    const line2 = parseLine(search.get('line_2'))
    const line2Label = search.get('line_2_label')
    const periodFilter = search.get('period_filter')
    const realEstates = search.get('real_estates')
    const segmentBy = search.get('segment_by')
    const toDate = search.get('to_date')

    if (fromDate) params.from_date = fromDate
    // REFERENCE-LINES
    if (line1 !== undefined) params.line_1 = line1
    if (line1Label) params.line_1_label = line1Label
    if (line2 !== undefined) params.line_2 = line2
    if (line2Label) params.line_2_label = line2Label

    if (
      periodFilter &&
      Object.values(PeriodFilter).includes(periodFilter as PeriodFilter)
    ) {
      params.period_filter = periodFilter as PeriodFilter
    }

    if (realEstates) {
      try {
        const parsed = JSON.parse(realEstates)

        if (Array.isArray(parsed) && parsed.length > 0) {
          params.real_estates = parsed.filter(
            (name): name is string =>
              typeof name === 'string' && name.length > 0
          )
        }
      } catch {
        // ignore malformed real_estates in URL
      }
    }

    if (segmentBy) params.segment_by = segmentBy
    if (toDate) params.to_date = toDate

    return params
  }, [location.search])

  const setQueryParams = useCallback(
    (params: TMetricQueryParams) => {
      const search = new URLSearchParams(location.search)

      // Clear all metric query param keys first
      PARAM_KEYS.forEach((key) => search.delete(key))

      // Set non-empty values
      if (params.filter) search.set('filter', JSON.stringify(params.filter))
      if (params.from_date) search.set('from_date', params.from_date)
      // REFERENCE-LINES
      if (params.line_1 !== undefined) {
        search.set('line_1', String(params.line_1))
      }
      if (params.line_1_label) {
        search.set('line_1_label', params.line_1_label)
      }
      if (params.line_2 !== undefined) {
        search.set('line_2', String(params.line_2))
      }
      if (params.line_2_label) {
        search.set('line_2_label', params.line_2_label)
      }
      if (params.period_filter)
        search.set('period_filter', params.period_filter)
      if (params.real_estates && params.real_estates.length > 0) {
        search.set('real_estates', JSON.stringify(params.real_estates))
      }
      if (params.segment_by) search.set('segment_by', params.segment_by)
      if (params.to_date) search.set('to_date', params.to_date)

      const qs = search.toString()

      history.replace({ search: qs ? `?${qs}` : '' })
    },
    [history, location.search]
  )

  return [queryParams, setQueryParams]
}
